import { ImageType, isAvailable } from "../../../types/baseTypes";

import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Pages } from "@/wp/types/acf-header-types";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import Split from "@/components/_primitives/Split/Split";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { ParentsType } from "@/wp/getParentPages";
import { Content } from "@/components/Content/Content";
import { useRouter } from "next/router";
import { getVideoData } from "@/getData/getVideoData";
import { useState, useEffect } from "react";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import { Headline } from "@/components/Headline/Headline";
import styles from "./SeiteHeader.module.scss";

export interface SeiteHeaderProps {
  post: Post<"page">;
  headerOptions?: HeadersOptionsType["header_pages"];
  parents: ParentsType;
}

export const isAnyImageAvailable = (
  post: Header_Pages["header_seite"],
): boolean => {
  return (
    isAvailable<ImageType>(post.media.image_fullhd) ||
    isAvailable<ImageType>(post.media.image_desktop) ||
    isAvailable<ImageType>(post.media.image_tablet) ||
    isAvailable<ImageType>(post.media.image_mobile)
  );
};

export const SeiteHeader = ({
  post,
  headerOptions,
  parents,
}: SeiteHeaderProps) => {
  if ("header_startseite" in post.acf) {
    throw new Error(
      "SeiteHeader cannot be used for post type with header_staseitert",
    );
  }
  const header = post.acf.header_seite;
  const router = useRouter();

  const {
    overline = "",
    subline = "",
    media,
    type,
    // background_color_class,
    section_margin_class,
    link,
    link_extended,
    content,
    headline_text_align_class,
    row_direction_class,
    seo_settings: { breadcrumb_title } = {},
  } = header;

  const {
    show_overline_subline,
    textheader,
    imageheader,
    splitheader,
    show_extended_link,
    breadcrumb_position,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  const {
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
    max_height: imageheaderMaxHeight,
  } = imageheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  const splitHeaderDirection: "row" | "row-reverse" =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  /* jscpd:ignore-start */
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );
  /* jscpd:ignore-end */

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItemResponsive
        image_desktop={media.image_desktop}
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        // mediaFormat={imageheaderMediaFormat}
        sizes="100vw"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
      />
    ) : media &&
      media.type === "video" &&
      videoData &&
      videoData.acf.header_videos.video_type === "upload" ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={media.autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );

  const contentJSX = (
    <>
      <Headline
        headline={header.title}
        headline_html_tag="h1-pages"
        extraClass={`is-flex is-flex-direction-column ${headline_text_align_class} is-align-items-center`}
        overline={showOverline ? overline : ""}
        subline={
          router.query.keyword
            ? (router.query.keyword as string)
            : showSubline
              ? subline
              : ""
        }
        disableAnimation
      />
      <Content
        headline=""
        headline_html_tag="h2"
        headline_text_align_class={headline_text_align_class}
        text_align_class={headline_text_align_class}
        button_type="primary"
        text={content}
        link={!show_extended_link && link}
        link_extended={show_extended_link && link_extended}
        isHeader={true}
      />
    </>
  );

  const breadcrumbsJSX = (className?: string) => (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        breadcrumbPositon={
          header.type === "splitheader" && splitHeaderDirection === "row"
            ? "is-justify-content-flex-end"
            : breadcrumb_position
              ? breadcrumb_position
              : "is-justify-content-flex-start"
        }
        breadcrumbTitle={breadcrumb_title}
        extraClass={`${className}`}
      />
    </div>
  );

  switch (type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${section_margin_class}`}
          header={
            <div
              className={`has-dunkelrot-bg text-header ${styles["text-header"]}`}
            >
              {breadcrumbsJSX()}
              <div className="section py-0">
                <div className="container">
                  <div className="columns is-centered">
                    <div
                      className={`column ${columnSize(textheaderTextWidth ?? false)}`}
                    >
                      <Headline
                        headline={header.title}
                        headline_html_tag="h1-posts"
                        extraClass={`is-flex is-flex-direction-column has-text-centered`}
                        // subline={overlineJSX}
                        disableAnimation
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          // content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <>
          <HeaderBase
            className={`${section_margin_class} ${styles["image-header"]}`}
            header={mediaJSX}
            content={contentJSX}
            headerFrame={"frameless"}
            contentFrame={"narrow-frame"}
            narrowFrameColumns={
              columnSize(imageheaderTextWidth ?? false) || "is-12"
            }
            contentHorizontalAlignment={
              imageheaderTextHorizontalAlign || "start"
            }
            contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
            height={
              imageheaderImageHeight === "screen-height"
                ? "screen-height"
                : imageheaderImageHeight === "custom-height" &&
                    imageheaderMaxHeight
                  ? "custom-height"
                  : "content-height"
            }
            heightDependency="content"
            arrangement={
              imageheaderVerticalAlign === "overlay"
                ? "overlay"
                : imageheaderVerticalAlign === "media-top-text-bottom"
                  ? "content-bottom"
                  : "content-top"
            }
            gradient={imageheaderHeadlineGradient ?? false}
            max_height={
              imageheaderImageHeight === "custom-height"
                ? Number(imageheaderMaxHeight)
                : undefined
            }
          />
          {breadcrumbsJSX(styles["breadcrumbs"])}
        </>
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX()
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={"min-height-acceptor-desktop"}
              second={contentJSX}
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX()
                  : undefined
              }
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
